import { useSyncExternalStore } from 'react'

/**
 * Raw media queries for the layouts that depend on the screen's height as well as its width (mobile review, rulings
 * M2 and M3). Chakra's breakpoints only look at the width, so a phone held sideways (844 x 390) would otherwise get the
 * roomy tablet layout with no field or button in its first screen. Use them as keys of a `css` object.
 *
 * - `COMPACT`: a phone. Narrower than `md` (768 px), or any width under 500 px tall (a phone in landscape).
 * - `ROOMY`: a tablet or a computer: from `md` and at least 500 px tall (the opposite of `COMPACT`).
 * - `SHORT`: under 500 px tall (a phone in landscape): decorations are hidden.
 * - `ROOMY_LOW`: roomy but under 700 px tall (a small laptop window): medium spacing.
 * - `NARROW`: under 360 px wide (the smallest phones, or 130 % zoom on a 375 px phone).
 */
/** `COMPACT` without its `@media` prefix, for `window.matchMedia`. */
export const COMPACT_QUERY = 'screen and (max-width: 47.99rem), screen and (max-height: 31.24rem)'
export const COMPACT = `@media ${COMPACT_QUERY}`
export const ROOMY = '@media screen and (min-width: 48rem) and (min-height: 31.25rem)'
export const SHORT = '@media screen and (max-height: 31.24rem)'
export const ROOMY_LOW = '@media screen and (min-width: 48rem) and (min-height: 31.25rem) and (max-height: 43.74rem)'
export const NARROW = '@media screen and (max-width: 22.49rem)'

/** Visually hidden but still read by screen readers (the same rules as Chakra's `VisuallyHidden`). */
export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
} as const

/**
 * True while a media query matches (`window.matchMedia`), kept in sync on resize and rotation. False where
 * `matchMedia` is missing. Read synchronously on the first render, so a phone never paints the desktop layout first.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null
      mq?.addEventListener?.('change', onChange)
      return () => mq?.removeEventListener?.('change', onChange)
    },
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false
  )
}

/** A phone (or a phone in landscape): the app shell with the bottom action area (`COMPACT`). */
export const useCompact = () => useMediaQuery(COMPACT_QUERY)
